import { useHotkeys } from 'react-hotkeys-hook';
import { CaptionContextType } from '@utils/providers/caption-context-state.ts';

type CaptionSegmentActions = Pick<
  CaptionContextType,
  | 'document'
  | 'clickedIndex'
  | 'setClickedIndex'
  | 'addListItem'
  | 'splitListItem'
  | 'removeListItem'
  | 'changeSegmentTimeForCurrentIndex'
> & {
  setTime: (seconds: number) => void;
  numpadDisabled: boolean;
};

const EDITABLE = { enableOnFormTags: true, enableOnContentEditable: true };

/**
 * Segment shortcuts shared by the Caption and Shared Caption editors (#4029).
 *
 * Notes on react-hotkeys-hook 4.x that shape these bindings:
 * - `numpad4` is normalised to `4`, so it also fires for the top-row 4 (and
 *   for "4"/"à" on AZERTY). Every numpad handler checks `event.code`.
 * - `option` is not a modifier keyword; `alt` is. `option+numpad4` never fired.
 * - Every alias in one binding that matches the event runs the callback, so
 *   brackets use the physical key names (`bracketleft`) instead of `[` plus
 *   aliases. `[` alone missed Ctrl+Shift+[ (`{`) and Mac Ctrl+Option+[ (`“`).
 * - No dependency arrays: the hook then always calls the latest callback, so a
 *   preference change or a document edit is picked up without a reload.
 */
export const useCaptionSegmentHotkeys = ({
  document: captionDocument,
  clickedIndex,
  setClickedIndex,
  addListItem,
  splitListItem,
  removeListItem,
  changeSegmentTimeForCurrentIndex,
  setTime,
  numpadDisabled
}: CaptionSegmentActions) => {
  const segments = captionDocument?.document ?? [];

  const withSegment = (action: (id: number) => void) => () => {
    if (clickedIndex === null) return;
    action(clickedIndex);
  };

  const replaySegment = () => {
    const segment = segments.find(s => s.id === clickedIndex);
    if (segment) setTime(segment.start / 1000);
  };

  const moveSelection = (step: 1 | -1) => {
    const currentIdx = segments.findIndex(s => s.id === clickedIndex);
    if (currentIdx === -1) return;
    const target = segments[currentIdx + step];
    if (!target) return;
    setClickedIndex(target.id);
    setTime(target.start / 1000);
    window.document
      .querySelector(`[data-index="${target.id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Numpad keys keep their default action (the digit is still typed) so the
  // default behaviour matches what existing numpad users rely on.
  const numpad =
    (code: string, action: () => void) => (event: KeyboardEvent) => {
      if (numpadDisabled || event.code !== code) return;
      action();
    };

  const laptop = (action: () => void) => (event: KeyboardEvent) => {
    event.preventDefault();
    action();
  };

  const insert = withSegment(addListItem);
  const split = withSegment(splitListItem);
  const remove = withSegment(removeListItem);
  const startBack = () => changeSegmentTimeForCurrentIndex('start', -50);
  const endForward = () => changeSegmentTimeForCurrentIndex('stop', 50);
  const fineStartPlus = () => changeSegmentTimeForCurrentIndex('start', 40);
  const fineStartMinus = () => changeSegmentTimeForCurrentIndex('start', -40);
  const fineEndPlus = () => changeSegmentTimeForCurrentIndex('stop', 40);
  const fineEndMinus = () => changeSegmentTimeForCurrentIndex('stop', -40);

  // Numpad shortcuts
  useHotkeys('numpad0', numpad('Numpad0', insert), EDITABLE);
  useHotkeys('alt+numpad0', numpad('Numpad0', remove), EDITABLE);
  useHotkeys(
    ['ctrl+numpad0', 'meta+numpad0'],
    numpad('Numpad0', split),
    EDITABLE
  );
  useHotkeys('numpad8', numpad('Numpad8', replaySegment), EDITABLE);
  useHotkeys('numpad4', numpad('Numpad4', startBack), EDITABLE);
  useHotkeys('numpad6', numpad('Numpad6', endForward), EDITABLE);
  useHotkeys('alt+numpad4', numpad('Numpad4', fineStartPlus), EDITABLE);
  useHotkeys('alt+numpad6', numpad('Numpad6', fineStartMinus), EDITABLE);
  useHotkeys('ctrl+numpad4', numpad('Numpad4', fineEndPlus), EDITABLE);
  useHotkeys('ctrl+numpad6', numpad('Numpad6', fineEndMinus), EDITABLE);

  // Laptop alternatives: always on, whatever the numpad preference
  useHotkeys(['ctrl+enter', 'meta+enter'], laptop(insert), EDITABLE);
  useHotkeys(['ctrl+shift+x', 'meta+shift+x'], laptop(split), EDITABLE);
  useHotkeys(
    [
      'ctrl+shift+backspace',
      'meta+shift+backspace',
      'ctrl+shift+delete',
      'meta+shift+delete'
    ],
    laptop(remove),
    EDITABLE
  );
  useHotkeys(['ctrl+shift+p', 'meta+shift+p'], laptop(replaySegment), EDITABLE);
  useHotkeys(
    ['ctrl+bracketleft', 'meta+bracketleft'],
    laptop(startBack),
    EDITABLE
  );
  useHotkeys(
    ['ctrl+bracketright', 'meta+bracketright'],
    laptop(endForward),
    EDITABLE
  );
  useHotkeys(
    ['ctrl+shift+bracketleft', 'meta+shift+bracketleft'],
    laptop(fineStartPlus),
    EDITABLE
  );
  useHotkeys(
    ['ctrl+shift+bracketright', 'meta+shift+bracketright'],
    laptop(fineStartMinus),
    EDITABLE
  );
  useHotkeys(
    ['ctrl+alt+bracketleft', 'meta+alt+bracketleft'],
    laptop(fineEndPlus),
    EDITABLE
  );
  useHotkeys(
    ['ctrl+alt+bracketright', 'meta+alt+bracketright'],
    laptop(fineEndMinus),
    EDITABLE
  );

  // Segment navigation
  useHotkeys(['meta+down', 'ctrl+down'], () => moveSelection(1));
  useHotkeys(['meta+up', 'ctrl+up'], () => moveSelection(-1));
};
